"use client";

import LoadingSpinner from "@components/loadingSpinner";
import { CheckIcon, ExclamationCircleIcon } from "@heroicons/react/24/outline";
import * as Form from "@radix-ui/react-form";
import { useNotificationStore } from "@store/notificationStore";
import { createBrowserClient } from "@supabase/ssr";
import { SUPABASE_ANON_KEY, SUPABASE_URL } from "@utils/constants";
import { useRouter, useSearchParams } from "next/navigation";
import { SyntheticEvent, useState } from "react";
import { handleOTPUser, handleSignInUser } from "../utils/signin";
import CodeInput from "./codeInput";
import { useLocale, useTranslations } from "next-intl";
import { updateUserLanguage } from "../utils/updateUserLanguage";
import {
  Text,
  Heading,
  Badge,
  Link,
  Button,
  CalloutRoot,
  CalloutText,
  CalloutIcon,
} from "@components/theming";
import { TextInput } from "@components/inputs";
import { AnimatePresence, motion } from "framer-motion";
import { useIsShopsite } from "@context/DomainContext";
import { joinClassNames } from "@utils/helpers";
import { useThemeContext } from "@utils/hooks";

interface Props {
  submitCallback?: () => Promise<void>;
  otpCallback?: () => Promise<void>;
  redirectTo?: string;
  next?: string;
  isCheckout?: boolean;
}

/**
 * Sign in component for user to authenticate to the website via supabase
 *
 */
export default function SignInForm({
  submitCallback,
  otpCallback,
  redirectTo,
  next,
  isCheckout,
}: Props) {
  const isShopsite = useIsShopsite();
  const signinTranslations = useTranslations("SignIn");
  const utilsTranslations = useTranslations("Utils");
  const locale = useLocale();
  const router = useRouter();
  const theme = useThemeContext();
  const searchParams = useSearchParams();
  const supabase = createBrowserClient(SUPABASE_URL!, SUPABASE_ANON_KEY!);
  const setFailure = useNotificationStore((state) => state.setFailure);
  const [loading, setLoading] = useState(false);
  const [otpLoading, setOTPLoading] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [otpError, setOtpError] = useState(false);
  const [otpEmail, setOtpEmail] = useState("");

  const otherParams = searchParams
    .toString()
    .split("&")
    .filter(
      (param) => !param.startsWith("redirectTo") && !param.startsWith("country")
    )
    .join("&");

  async function otpSubmit(event: SyntheticEvent<HTMLFormElement>) {
    setOTPLoading(true);
    event.preventDefault();
    const { email } = Object.fromEntries(new FormData(event.currentTarget));
    const encodedEmail = encodeURIComponent(email as string);
    const signUpHref = redirectTo
      ? `${
          window.location.origin
        }/account/sign-up?redirectTo=${encodeURIComponent(
          redirectTo
        )}&email=${encodedEmail}`
      : `${window.location.origin}/account/sign-up?email=${encodedEmail}`;
    const otpResponse = await handleOTPUser(
      event,
      supabase,
      signUpHref,
      isCheckout
    );
    setOTPLoading(false);
    if (otpResponse && otpResponse.status === "failure") {
      setFailure(utilsTranslations("somethingwentwrong"));
      setOtpError(true);
      return;
    }

    if (otpCallback) {
      await otpCallback();
    }

    setOtpEmail(email as string);
    return;
  }

  async function handleSubmit(event: SyntheticEvent<HTMLFormElement>) {
    setLoading(true);
    event.preventDefault();

    const signInResponse = await handleSignInUser(event, supabase);

    if (
      signInResponse.status !== "success" &&
      signInResponse?.status === "failure"
    ) {
      setLoading(false);
      setPasswordError(true);
      setFailure(signInResponse.message);
      return;
    }

    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (session && session.access_token) {
      await updateUserLanguage(locale, session.access_token);
    }

    // callback func
    if (submitCallback) {
      await submitCallback();
    }

    if (redirectTo) {
      router.replace(`${redirectTo}?${otherParams}`);
    }
    router.refresh();
  }

  return (
    <AnimatePresence mode="wait">
      {otpEmail ? (
        <motion.div
          key="otpEmail"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="flex flex-col gap-8">
            <CalloutRoot color="green" shopsiteClasses="flex-col !items-start">
              <div className="flex items-center gap-2">
                <CalloutIcon>
                  <CheckIcon className="h-6 w-6" />
                </CalloutIcon>
                <CalloutText
                  size="4"
                  weight="medium"
                  shopsiteClasses="font-semibold"
                >
                  {signinTranslations("codesent")}
                </CalloutText>
              </div>
              <CalloutText size="2" shopsiteClasses="text-sm">
                {signinTranslations("codesentdesc")}
              </CalloutText>
            </CalloutRoot>
            <CodeInput
              email={otpEmail}
              submitCallback={submitCallback}
              next={next}
            />
          </div>
        </motion.div>
      ) : (
        <motion.div
          key="form"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="flex flex-col gap-8">
            <Form.Root
              onSubmit={handleSubmit}
              onClearServerErrors={() => setPasswordError(false)}
            >
              <div className="flex flex-col gap-4">
                <Form.Field
                  name="email"
                  serverInvalid={passwordError}
                  className="flex flex-col gap-1"
                >
                  <div className="flex items-center justify-between gap-2 h-6">
                    <Form.Label asChild htmlFor="email">
                      <Text
                        as="label"
                        weight="medium"
                        shopsiteClasses="text-black font-medium"
                      >
                        {signinTranslations("email")}
                      </Text>
                    </Form.Label>
                    <Form.ValidityState>
                      {(validity) =>
                        (validity?.typeMismatch ||
                          validity?.valueMissing ||
                          passwordError) && (
                          <Badge
                            color="red"
                            radius={
                              theme && theme.radius === "none" ? "none" : "full"
                            }
                          >
                            <ExclamationCircleIcon className="h-4 w-4 stroke-2" />
                            <Text as="span" size="2">
                              {validity?.valueMissing
                                ? utilsTranslations("required")
                                : validity?.typeMismatch
                                ? utilsTranslations("invalidemail")
                                : utilsTranslations("invalidlogin")}
                            </Text>
                          </Badge>
                        )
                      }
                    </Form.ValidityState>
                  </div>
                  <Form.Control
                    id="email"
                    name="email"
                    autoComplete="email"
                    data-testid="email"
                    type="email"
                    onChange={() => setPasswordError(false)}
                    required
                    asChild
                  >
                    <TextInput size="3" />
                  </Form.Control>
                </Form.Field>
                <div className="flex flex-col gap-2">
                  <Form.Field name="password" className="flex flex-col gap-1">
                    <div className="flex items-center justify-between h-6">
                      <Form.Label htmlFor="password" asChild>
                        <Text
                          as="label"
                          weight="medium"
                          shopsiteClasses="text-black font-medium"
                        >
                          {signinTranslations("password")}
                        </Text>
                      </Form.Label>
                      <Form.Message match="valueMissing" asChild>
                        <Badge
                          color="red"
                          radius={
                            theme && theme.radius === "none" ? "none" : "full"
                          }
                        >
                          <ExclamationCircleIcon className="h-4 w-4 stroke-2" />
                          <Text as="span" size="2">
                            {utilsTranslations("required")}
                          </Text>
                        </Badge>
                      </Form.Message>
                    </div>
                    <Form.Control
                      id="password"
                      name="password"
                      data-testid="password"
                      type="password"
                      required
                      asChild
                    >
                      <TextInput size="3" />
                    </Form.Control>
                  </Form.Field>
                  <Link
                    weight="medium"
                    href="/account/reset-password"
                    sharedClasses="self-end"
                    size="2"
                    shopsiteClasses="text-sm text-indigo-500 font-medium"
                  >
                    {signinTranslations("forgotpassword")}
                  </Link>
                </div>
                <Form.Submit
                  data-testid="submit"
                  disabled={loading || otpLoading}
                  asChild
                >
                  <Button size="3" sharedClasses="w-full">
                    {loading ? (
                      <div className="flex justify-center items-center gap-2">
                        <LoadingSpinner
                          size="18"
                          color={isShopsite ? "#ffffff" : "var(--gray-12)"}
                        />
                        <Text as="span">{signinTranslations("signingin")}</Text>
                      </div>
                    ) : (
                      <Text as="span">{signinTranslations("signin")}</Text>
                    )}
                  </Button>
                </Form.Submit>
              </div>
            </Form.Root>
            <div
              className={joinClassNames(
                "relative w-full border-t flex-1",
                isShopsite
                  ? "border-t-gray-400"
                  : "border-t-[color:var(--gray-a5)]"
              )}
            />
            <Form.Root
              onSubmit={otpSubmit}
              onClearServerErrors={() => setOtpError(false)}
            >
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-3">
                  <Heading
                    as="h2"
                    size="5"
                    shopsiteClasses="text-lg text-black font-medium"
                  >
                    {signinTranslations("signinOTP")}
                  </Heading>
                  <Text color="gray" shopsiteClasses="text-gray-500 text-sm">
                    {signinTranslations.rich("otpdesc", {
                      link: (chunks) => (
                        <Link
                          shopsiteClasses="text-indigo-400 underline"
                          href={{
                            pathname: "/account/sign-up",
                            query: redirectTo
                              ? `redirectTo=${redirectTo}`
                              : undefined,
                          }}
                        >
                          {chunks}
                        </Link>
                      ),
                    })}
                  </Text>
                </div>
                <Form.Field
                  name="email"
                  serverInvalid={otpError}
                  className="flex flex-col gap-1"
                >
                  <div className="flex justify-between items-center h-6">
                    <Form.Label asChild htmlFor="email">
                      <Text
                        as="label"
                        weight="medium"
                        shopsiteClasses="text-black font-medium"
                      >
                        {signinTranslations("email")}
                      </Text>
                    </Form.Label>
                    <Form.ValidityState>
                      {(validity) =>
                        (validity?.typeMismatch ||
                          validity?.valueMissing ||
                          otpError) && (
                          <Badge
                            color="red"
                            radius={
                              theme && theme.radius === "none" ? "none" : "full"
                            }
                          >
                            <ExclamationCircleIcon className="h-4 w-4 stroke-2" />
                            <Text as="span" size="2">
                              {validity?.valueMissing
                                ? utilsTranslations("required")
                                : validity?.typeMismatch
                                ? utilsTranslations("invalidemail")
                                : utilsTranslations("somethingwentwrong")}
                            </Text>
                          </Badge>
                        )
                      }
                    </Form.ValidityState>
                  </div>
                  <Form.Control
                    id="otp_email"
                    name="email"
                    autoComplete="email"
                    data-testid="email"
                    type="email"
                    onChange={() => setOtpError(false)}
                    required
                    asChild
                  >
                    <TextInput size="3" />
                  </Form.Control>
                </Form.Field>
                <Form.Submit asChild disabled={otpLoading || loading}>
                  <Button variant="soft" shopsiteVariant="soft" size="3">
                    {otpLoading ? (
                      <div className="flex justify-center items-center gap-2">
                        <LoadingSpinner
                          size="18"
                          color={isShopsite ? "#3730a3b3" : "var(--gray-12)"}
                        />
                        <Text as="span">
                          {signinTranslations("sendingotp")}
                        </Text>
                      </div>
                    ) : (
                      <Text as="span">{signinTranslations("requestOTP")}</Text>
                    )}
                  </Button>
                </Form.Submit>
              </div>
            </Form.Root>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
